import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import Eyebrow from "../../components/brand/Eyebrow";
import BudgetFormatEditor from "./BudgetFormatEditor";
import {
  createBudget,
  createFiscalYear,
  getBudgets,
  getFiscalYears,
  updateFiscalYear,
} from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import {
  defaultBudgetName,
  fiscalYearClash,
  fiscalYearLabel,
  formatIsoUk,
  suggestedFiscalYearDates,
} from "./fiscalYearDates";

const STEPS = ["Financial year", "Charges", "Draft budget"];

function WizardStepIcon({ active, completed, icon }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        bgcolor: completed ? "success.main" : active ? "text.primary" : "action.selected",
        color: completed || active ? "common.white" : "text.secondary",
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 18 }} /> : icon}
    </Box>
  );
}

const chargeSummary = (template) => {
  const lines = [];
  (template?.sections || []).forEach((section) => {
    const names = (section.lines || []).map((line) => line.line_item_name).filter(Boolean);
    if (section.section_type === "block") {
      lines.push(`${section.name || "Block charge"}: ${names.join(", ") || "no charges selected"}`);
    } else {
      names.forEach((name) => lines.push(name));
    }
  });
  return lines;
};

export default function CreateBudgetWizard({
  open,
  onClose,
  propertyId,
  propertyName,
  financialYearStartDay,
  financialYearStartMonth,
  onCreated,
  onSavedProgress,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const formatRef = useRef(null);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [years, setYears] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [mode, setMode] = useState("existing");
  const [selectedId, setSelectedId] = useState("");
  const [dates, setDates] = useState({ start_date: "", end_date: "" });
  const [markCurrent, setMarkCurrent] = useState(true);
  const [yearTouched, setYearTouched] = useState(false);
  const [createdYear, setCreatedYear] = useState(null);
  const [year, setYear] = useState(null);
  const [chargesMounted, setChargesMounted] = useState(false);
  const [template, setTemplate] = useState(null);
  const [budgetName, setBudgetName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [savedProgress, setSavedProgress] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    setStep(0);
    setError("");
    setBusy(false);
    setYearTouched(false);
    setCreatedYear(null);
    setYear(null);
    setChargesMounted(false);
    setTemplate(null);
    setBudgetName("");
    setNameTouched(false);
    setSavedProgress(false);
    setConfirmDiscard(false);

    const load = async () => {
      try {
        setLoading(true);
        const [yearRes, budgetRes] = await Promise.all([
          getFiscalYears(propertyId),
          getBudgets(propertyId, 1, { page_size: 100 }),
        ]);
        if (ignore) return;
        const nextYears = yearRes.data.results || [];
        const nextBudgets = budgetRes.data.results || [];
        setYears(nextYears);
        setBudgets(nextBudgets);
        const suggested = suggestedFiscalYearDates(
          financialYearStartDay,
          financialYearStartMonth,
          nextYears
        );
        setDates(suggested);
        const hasCurrent = nextYears.some((item) => item.is_current);
        setMarkCurrent(!hasCurrent);
        if (nextYears.length === 0) {
          setMode("new");
          setSelectedId("");
        } else {
          const current = nextYears.find((item) => item.is_current) || nextYears[0];
          setMode("existing");
          setSelectedId(String(current.id));
        }
      } catch (e) {
        if (!ignore) setError(getApiErrorMessage(e, "Failed to load financial years."));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [open, propertyId, financialYearStartDay, financialYearStartMonth]);

  const hasCurrentYear = years.some((item) => item.is_current);
  const budgetsForYear = budgets.filter(
    (budget) => year && String(budget.fiscal_year?.id) === String(year.id)
  );

  const finishClose = () => {
    if (savedProgress) {
      onSavedProgress?.(
        "The year or charges you added are saved. You can finish the budget later."
      );
    }
    onClose();
  };

  const requestClose = () => {
    if (busy) return;
    const dirty =
      step === 0
        ? yearTouched
        : step === 1
          ? Boolean(formatRef.current?.isDirty())
          : nameTouched;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    finishClose();
  };

  const continueYear = async () => {
    setError("");
    if (mode === "existing") {
      const selected = years.find((item) => String(item.id) === String(selectedId));
      if (!selected) {
        setError("Choose a financial year.");
        return;
      }
      setYear(selected);
      setChargesMounted(true);
      setStep(1);
      return;
    }

    if (
      createdYear &&
      createdYear.start_date === dates.start_date &&
      createdYear.end_date === dates.end_date
    ) {
      setYear(createdYear);
      setChargesMounted(true);
      setStep(1);
      return;
    }

    const clash = fiscalYearClash(dates.start_date, dates.end_date, years);
    if (clash) {
      setError(clash);
      return;
    }

    try {
      setBusy(true);
      const res = await createFiscalYear(propertyId, dates);
      let nextYear = res.data;
      setYears((current) => [nextYear, ...current.filter((item) => item.id !== nextYear.id)]);
      setCreatedYear(nextYear);
      setSelectedId(String(nextYear.id));
      setMode("existing");
      setYearTouched(false);
      setSavedProgress(true);
      if (markCurrent && !hasCurrentYear) {
        const updated = await updateFiscalYear(propertyId, nextYear.id, { is_current: true });
        nextYear = updated.data;
        setCreatedYear(nextYear);
        setYears((current) => [nextYear, ...current.filter((item) => item.id !== nextYear.id)]);
      }
      setYear(nextYear);
      setYearTouched(false);
      setChargesMounted(true);
      setStep(1);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create the financial year."));
    } finally {
      setBusy(false);
    }
  };

  const continueCharges = async () => {
    setError("");
    if (!formatRef.current?.isReady()) {
      setError("The charges are still loading.");
      return;
    }
    try {
      setBusy(true);
      const saved = await formatRef.current.save();
      if (!saved) return;
      setTemplate(saved);
      setSavedProgress(true);
      if (!nameTouched) setBudgetName(defaultBudgetName(year));
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    setError("");
    try {
      setBusy(true);
      const res = await createBudget(propertyId, {
        name: budgetName.trim(),
        fiscal_year_id: year.id,
        template_id: template.id,
      });
      setSavedProgress(false);
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create the budget."));
    } finally {
      setBusy(false);
    }
  };

  const onContinue = () => {
    if (step === 0) continueYear();
    else if (step === 1) continueCharges();
    else createDraft();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Create budget for {propertyName || "this property"}</DialogTitle>
        <DialogContent dividers>
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{
              mb: 3,
              "& .MuiStepLabel-label.Mui-completed": {
                color: "success.main",
                fontWeight: 600,
              },
              "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: "success.main",
              },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={WizardStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {loading ? (
            <CircularProgress size={28} />
          ) : (
            <>
              {step === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {mode === "existing" ? (
                    <>
                      <Typography variant="body2">
                        Use a financial year that is already on this property, or create the next one.
                      </Typography>
                      <RadioGroup
                        value={selectedId}
                        onChange={(event) => setSelectedId(event.target.value)}
                      >
                        {years.map((item) => (
                          <FormControlLabel
                            key={item.id}
                            value={String(item.id)}
                            control={<Radio />}
                            label={`${fiscalYearLabel(item)}${item.is_current ? " (current)" : ""}`}
                          />
                        ))}
                      </RadioGroup>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setMode("new");
                          setYearTouched(false);
                          setDates(
                            suggestedFiscalYearDates(
                              financialYearStartDay,
                              financialYearStartMonth,
                              years
                            )
                          );
                        }}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Create a new financial year
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">
                        {years.length === 0
                          ? "This property has no financial year yet. Add the first one."
                          : "Add a new 12-month financial year."}
                      </Typography>
                      {!financialYearStartDay || !financialYearStartMonth ? (
                        <Alert severity="info">
                          This property has no usual year start, so enter the dates.
                        </Alert>
                      ) : null}
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                          label="Start date"
                          type="date"
                          value={dates.start_date}
                          onChange={(event) => {
                            setYearTouched(true);
                            setDates((current) => ({ ...current, start_date: event.target.value }));
                          }}
                          InputLabelProps={{ shrink: true }}
                          helperText={dates.start_date ? formatIsoUk(dates.start_date) : " "}
                        />
                        <TextField
                          label="End date"
                          type="date"
                          value={dates.end_date}
                          onChange={(event) => {
                            setYearTouched(true);
                            setDates((current) => ({ ...current, end_date: event.target.value }));
                          }}
                          InputLabelProps={{ shrink: true }}
                          helperText={dates.end_date ? formatIsoUk(dates.end_date) : " "}
                        />
                      </Box>
                      {hasCurrentYear ? (
                        <Alert severity="info">
                          Invoices will still use the current financial year.
                        </Alert>
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={markCurrent}
                              onChange={(event) => {
                                setYearTouched(true);
                                setMarkCurrent(event.target.checked);
                              }}
                            />
                          }
                          label="Make this the current financial year"
                        />
                      )}
                      {years.length > 0 && (
                        <Button
                          startIcon={<ArrowBackIcon />}
                          onClick={() => {
                            setMode("existing");
                            setYearTouched(false);
                          }}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Use an existing financial year
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              )}

              {chargesMounted && (
                <Box sx={{ display: step === 1 ? "block" : "none" }}>
                  <BudgetFormatEditor
                    ref={formatRef}
                    embedded
                    propertyId={propertyId}
                    propertyName={propertyName}
                    onError={setError}
                  />
                </Box>
              )}

              {step === 2 && year && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.brand?.border || theme.palette.divider}`,
                      borderRadius: 1,
                      overflow: "hidden",
                    })}
                  >
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.brand?.border || theme.palette.divider}`,
                      })}
                    >
                      <Eyebrow color="text.secondary" sx={{ mb: 0.75 }}>
                        Financial year
                      </Eyebrow>
                      <Typography variant="body1">{fiscalYearLabel(year)}</Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Eyebrow color="text.secondary" sx={{ mb: 0.75 }}>
                        Charges
                      </Eyebrow>
                      {chargeSummary(template).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No charges selected.
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {chargeSummary(template).map((line, index) => (
                            <Typography key={`${line}-${index}`} variant="body2">
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {budgetsForYear.length > 0 && (
                    <Alert severity="warning">
                      This year already has {budgetsForYear.map((budget) => budget.name).join(", ")}.
                      You can still create another draft.
                    </Alert>
                  )}
                  <TextField
                    label="Budget name"
                    value={budgetName}
                    onChange={(event) => {
                      setNameTouched(true);
                      setBudgetName(event.target.value);
                    }}
                    fullWidth
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                    Saved as a draft. You can enter amounts and send it for approval afterwards.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button startIcon={<CloseIcon />} onClick={requestClose} disabled={busy}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {step > 0 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setError("");
                setStep((current) => current - 1);
              }}
              disabled={busy}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : step === 2 ? (
                <AddIcon />
              ) : (
                <ArrowForwardIcon />
              )
            }
            onClick={onContinue}
            disabled={busy || loading}
          >
            {step === 2 ? "Create draft budget" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>Discard these edits?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The changes on this step have not been saved. A financial year or charge list you
            already saved will stay on the property.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setConfirmDiscard(false)}>
            Keep editing
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={() => {
              setConfirmDiscard(false);
              finishClose();
            }}
          >
            Discard edits
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
